// Today selection (spec §8.2/§8.3). Pure so it can be unit-tested.
import type { TaskResource } from '@/kinds/task/apply'
import { isTemplate } from '@/kinds/task/schema'
import type { Picker } from '@/state/ui'

export interface TodaySelection {
  /** the capped list to show */
  picked: TaskResource[]
  /** everything eligible (after picker filter), in order */
  all: TaskResource[]
  /** due before today and not done: shown as a single calm row */
  slipped: TaskResource[]
  /** eligible before the picker filter — for "nothing matches" messaging */
  eligible: number
}

const estimate = (t: TaskResource) => t.spec.estimate_minutes ?? 0

/** started first, then priority desc, then smallest estimate (none last), then id. */
export const compareToday = (a: TaskResource, b: TaskResource): number => {
  const sa = a.status.started_at ? 1 : 0
  const sb = b.status.started_at ? 1 : 0
  if (sa !== sb) return sb - sa
  if (a.spec.priority !== b.spec.priority) return b.spec.priority - a.spec.priority
  const ea = estimate(a)
  const eb = estimate(b)
  if (ea !== eb) {
    if (!ea) return 1
    if (!eb) return -1
    return ea - eb
  }
  return a.id < b.id ? -1 : 1
}

export const matchesPicker = (t: TaskResource, picker: Picker): boolean => {
  if (picker.time !== 'any') {
    const e = t.spec.estimate_minutes
    if (!e || e > picker.time) return false
  }
  if (picker.energy !== 'any' && t.labels.energy !== picker.energy) return false
  return true
}

export const selectToday = (
  tasks: TaskResource[],
  opts: { today: string; cap: number; picker: Picker; activeId?: string | null },
): TodaySelection => {
  const open = tasks.filter((t) => !t.deleted_at && !isTemplate(t.spec))
  const candidates = open.filter(
    (t) => t.status.state === 'next' && !!t.spec.scheduled && t.spec.scheduled <= opts.today,
  )
  const all = candidates.filter((t) => matchesPicker(t, opts.picker)).sort(compareToday)
  let picked = all.slice(0, Math.max(1, opts.cap))
  if (opts.activeId) {
    const active = all.find((t) => t.id === opts.activeId)
    if (active) picked = [active]
  }
  const slipped = open
    .filter(
      (t) =>
        t.status.state !== 'done' && t.status.state !== 'dropped' && !!t.spec.due && t.spec.due < opts.today,
    )
    .sort((a, b) => (a.spec.due! < b.spec.due! ? -1 : 1))
  return { picked, all, slipped, eligible: candidates.length }
}
